import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm } from 'redux-form';

import type SaleRequestModel from 'ab-requestModels/sale.requestModel';

import { CountryCode } from '@acceligentllc/shared/enums/countryCode';

import type { RootState } from 'af-reducers';

import { toRequestModelWithRawPhoneNumbers } from 'af-utils/form.util';

import { SALE_CREATE } from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import Breadcrumbs from 'af-components/Breadcrumbs';
import CreateSaleForm from 'af-components/SharedForms/Resources/Sale';
import { validate, warn } from 'af-components/SharedForms/Resources/Sale/validation';

import * as SaleActions from 'af-actions/sale';

type OwnProps = CustomRouteComponentProps<{ id: undefined; }>;

type FormProps = InjectedFormProps<SaleRequestModel, FormOwnProps>;

type FormOwnProps = OwnProps & ConnectedProps<typeof connector>;

type Props = FormOwnProps & FormProps;

const CreateSale: React.FC<Props> = (props) => {
	const { createSale, location: { state: { orgAlias } }, companyName, history } = props;

	const onCreateSale = React.useCallback(async (form: SaleRequestModel) => {
		const data: SaleRequestModel = toRequestModelWithRawPhoneNumbers(form, 'phone', 'fax');
		await createSale(data);
		history.push(CLIENT.COMPANY.RESOURCES.SALE.LIST(orgAlias, companyName));
	}, [companyName, createSale, history, orgAlias]);

	return (
		<div className="form-segment">
			<Breadcrumbs
				items={
					[
						{ label: 'Sale', url: CLIENT.COMPANY.RESOURCES.SALE.LIST(orgAlias, companyName) },
						{ label: 'New Sale' },
					]
				}
			/>
			<CreateSaleForm {...props} onSubmit={onCreateSale} />
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
		initialValues: {
			faxCountryCode: CountryCode.US,
			phoneCountryCode: CountryCode.US,
		},
	};
}

function mapDispatchToProps() {
	return {
		createSale: SaleActions.create,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector,
	reduxForm<SaleRequestModel, FormOwnProps>({ form: SALE_CREATE, validate, warn, enableReinitialize: true })
);

export default enhance(CreateSale);
